import React from "react";
import styled from "styled-components";
import { ResponsiveRadar } from "@nivo/radar";

import {
  Card,
  HorizontalLine,
  RowContainer,
} from "../../../../components/containers/styledComponents";
import { CompassParagraph } from "../../../../components/common/styledComponents";
import {
  compassLightGreen,
  darkGreen,
  greyText,
  lightGreyOutline,
  lightGreyText,
} from "../../../../styles";

const ChartWrap = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding-left: 14px;

  & + & {
    border-left: 1px solid ${lightGreyOutline};
  }
  &:first-child {
    padding-left: 0;
  }
`;

const StatValue = styled(CompassParagraph)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type RadarDatum = { pillar: string; score: number } & Record<string, unknown>;

export const PillarRadarCard = ({
  data,
  highest,
  lowest,
  radarColor = compassLightGreen,
  w = 100,
  m,
  isLoading,
}: PillarRadarCardProps): JSX.Element => {
  const scores = data.map((d) => d.score);
  const mean =
    scores.length > 0 ? scores.reduce((s, v) => s + v, 0) / scores.length : 0;
  const cv =
    mean > 0
      ? Math.sqrt(
          scores.reduce((s, v) => s + (v - mean) ** 2, 0) / scores.length,
        ) / mean
      : 0;
  const balance = cv <= 0.15 ? "Balanced" : cv <= 0.35 ? "Uneven" : "Lopsided";

  return (
    <Card
      isLoading={isLoading}
      w={w}
      m={m}
      h="520px"
      style={{ display: "flex", flexDirection: "column", minHeight: 0 }}
    >
      <RowContainer mt={0} mb={0} jc="space-between" ai="center">
        <CompassParagraph fontSize={16} bold color={darkGreen}>
          Pillar Radar
        </CompassParagraph>
        <CompassParagraph fontSize={12} color={greyText}>
          {data.length} axes
        </CompassParagraph>
      </RowContainer>
      <HorizontalLine color="#E2E8F0" marginTop={4} marginBottom={0} />

      <ChartWrap>
        <ResponsiveRadar<RadarDatum>
          data={data as RadarDatum[]}
          keys={["score"]}
          indexBy="pillar"
          maxValue={100}
          gridLevels={3}
          margin={{ top: 34, right: 62, bottom: 26, left: 62 }}
          borderColor={radarColor}
          borderWidth={1.5}
          gridShape="linear"
          enableDots={false}
          colors={[radarColor]}
          fillOpacity={0.22}
          blendMode="multiply"
          motionConfig="gentle"
          sliceTooltip={(): null => null}
          legends={[]}
          theme={{
            axis: {
              ticks: {
                text: {
                  fontSize: 10,
                  fill: lightGreyText,
                  fontFamily: "Apercu, sans-serif",
                },
              },
            },
          }}
        />
      </ChartWrap>

      <HorizontalLine color="#E2E8F0" marginTop={0} marginBottom={14} />
      <StatRow>
        <Stat>
          <StatValue fontSize={14} bold color={greyText} mr={0}>
            {highest}
          </StatValue>
          <CompassParagraph fontSize={12} color={greyText} mr={0}>
            Highest
          </CompassParagraph>
        </Stat>
        <Stat>
          <StatValue fontSize={14} bold color={greyText} mr={0}>
            {lowest}
          </StatValue>
          <CompassParagraph fontSize={12} color={greyText} mr={0}>
            Lowest
          </CompassParagraph>
        </Stat>
        <Stat>
          <StatValue fontSize={14} bold color={greyText} mr={0}>
            {balance}
          </StatValue>
          <CompassParagraph fontSize={12} color={greyText} mr={0}>
            Balance
          </CompassParagraph>
        </Stat>
      </StatRow>
    </Card>
  );
};

type PillarRadarCardProps = {
  data: { pillar: string; score: number }[];
  highest: string;
  lowest: string;
  trend?: string;
  radarColor?: string;
  w?: number;
  m?: string;
  isLoading?: boolean;
};
